// useOutbox — the React seam over the persisted send queue
// (Brief 5 / point 15, the comms artery).
//
// Gives a composer one `send(body)` that NEVER loses the message:
//   1. enqueue it (persisted) BEFORE the wire is touched,
//   2. try to send immediately with the entry's idempotency key,
//   3. on success → clear the entry + hand the server row to `onSent`,
//   4. on failure → leave it queued (shown pending), auto-retried on reconnect.
// Plus it exposes this thread's `pending` entries and surfaces the queue to the
// browser's reconnection signals: an `online` event or the tab returning to the
// foreground (`visibilitychange`) flushes the backlog. A single in-flight guard
// means the immediate send, a reconnect flush, and a cross-tab change can never
// double-drain the same entry.
//
// The wire is the INJECTED client (`useApi().sendThreadMessage`); an
// explicit `sendImpl` overrides it for unit tests against a fake wire — the same
// injection seam the rest of the shared layer uses.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage } from "@waytide/core";
import { useApi } from "../api";
import {
  enqueue,
  flushOutbox,
  readOutbox,
  readThreadOutbox,
  retry as retryEntry,
  subscribeOutbox,
  type OutboxEntry,
} from "../model/outbox";

/** Safety-net retry cadence for a backlog the reconnect events never re-trigger
 * (a 5xx while the browser stays `online`). Slow on purpose — the event-driven
 * triggers handle the common case; this only catches the silent-failure tail. */
const RETRY_INTERVAL_MS = 15000;

/** The wire a flush uses — the injected `sendThreadMessage`, or a fake in tests.
 * It MUST forward the idempotency key (4th arg) so a replay is dedupable. */
export type SendThreadMessage = (
  threadId: number,
  body: string,
  aside?: boolean,
  idempotencyKey?: string,
) => Promise<ChatMessage>;

export interface UseOutbox {
  /** This thread's still-unsent messages, oldest-first (render as pending). */
  pending: OutboxEntry[];
  /** Queue + try to send a message. Resolves once the attempt settles; the
   * message is safe (persisted) regardless of the outcome. */
  send: (body: string, aside?: boolean) => Promise<void>;
  /** Force a replay of the whole queue now (also auto-called on reconnect). */
  flush: () => Promise<void>;
  /** Re-arm a parked (permanently-failed) entry and attempt it again — the
   * "Retry" affordance on a `failed` pending message. */
  retry: (key: string) => void;
}

/**
 * @param threadId the thread this composer targets; null disables sending (and
 *   yields an empty pending list) until a thread is known.
 * @param onSent fired with the server-stamped row on each confirmed success, so
 *   the channel can merge it (de-duped on id) without a re-read.
 * @param sendImpl injected wire — defaults to the app-provided client
 *   (`useApi().sendThreadMessage`); pass a fake in tests.
 */
export function useOutbox(
  threadId: number | null,
  onSent?: (m: ChatMessage) => void,
  sendImpl?: SendThreadMessage,
): UseOutbox {
  const api = useApi();
  const send_ = sendImpl ?? api.sendThreadMessage;
  const [pending, setPending] = useState<OutboxEntry[]>(() =>
    threadId == null ? [] : readThreadOutbox(threadId),
  );

  // Refs so the stable callbacks read current values without re-binding.
  const threadRef = useRef(threadId);
  const onSentRef = useRef(onSent);
  const sendRef = useRef(send_);
  const alive = useRef(true);
  threadRef.current = threadId;
  onSentRef.current = onSent;
  sendRef.current = send_;

  // A flush awaits the wire; if the composer unmounts mid-send, the settled
  // callbacks must NOT setState on the dead component (the send itself still
  // completes + clears the durable entry — only the UI update is skipped).
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    if (!alive.current) return;
    const t = threadRef.current;
    setPending(t == null ? [] : readThreadOutbox(t));
  }, []);

  // Re-derive this thread's pending list whenever the queue changes (this tab
  // or another) or the thread switches.
  useEffect(() => {
    refresh();
    return subscribeOutbox(refresh);
  }, [refresh, threadId]);

  // Drain the queue through the injected wire. Concurrency is handled by the
  // store's MODULE-LEVEL single-flight lock (so two instances / two reconnect
  // signals can't double-send); this just always asks for a drain and refreshes
  // the pending view when it settles.
  const flush = useCallback(async () => {
    try {
      await flushOutbox(
        (entry) =>
          sendRef.current(entry.threadId, entry.body, entry.aside, entry.key),
        ({ entry, sent }) => {
          // `sent` is the full ChatMessage from the real wire; the store only
          // needs its id, so we cast back to hand the row to the consumer.
          void entry;
          onSentRef.current?.(sent as ChatMessage);
        },
      );
    } finally {
      refresh();
    }
  }, [refresh]);

  // The send a composer calls: persist first (never lose it), then drain — the
  // new entry is part of the queue, so a single flush sends it (and any older
  // backlog) in order.
  const send = useCallback(
    async (body: string, aside?: boolean) => {
      const t = threadRef.current;
      if (t == null) return;
      enqueue({ threadId: t, body, aside });
      refresh();
      await flush();
    },
    [flush, refresh],
  );

  // Re-arm a parked entry and immediately try to send it again.
  const retry = useCallback(
    (key: string) => {
      retryEntry(key);
      refresh();
      void flush();
    },
    [flush, refresh],
  );

  // Reconnect triggers: back online, or the tab returns to the foreground (a
  // backgrounded app that missed the `online` event still flushes on focus).
  // PLUS a slow safety-net interval: a send can fail while the browser stays
  // `online` (a server 5xx, a flaky proxy) — no `online`/`visibilitychange` ever
  // fires, so without this a failed entry would sit forever. The interval re-
  // attempts only while a backlog exists, so a healthy idle composer does no
  // work.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOnline = () => void flush();
    const onVisible = () => {
      if (document.visibilityState === "visible") void flush();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    const retryTimer = setInterval(() => {
      // Only re-attempt if there's a SENDABLE entry (not one parked as failed) —
      // a queue of only-parked entries needs a manual retry, not a wire poll.
      if (readOutbox().some((e) => !e.failed)) void flush();
    }, RETRY_INTERVAL_MS);
    // A composer mounting while there's already a backlog (e.g. after a reload
    // that re-hydrated the queue) should attempt a drain right away.
    void flush();
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(retryTimer);
    };
  }, [flush]);

  return { pending, send, flush, retry };
}
